import CREW_COLORS from '../Routing/TerritoryWorkbench/crewColors'
import { HMI_YARD, haversineMiles } from '../Routing/TerritoryWorkbench/mapUtils'

export const V4_T = {
  cream: '#f4efe4',
  surface: '#fbf8f1',
  surface2: '#faf6ee',
  line: '#e7dfc9',
  lineSoft: '#ede6d2',
  ink: '#1c2419',
  ink2: '#3d463a',
  mute: '#6f7a6b',
  muteSoft: '#97a094',
  green: '#1f5e3a',
  greenDeep: '#15422a',
  greenSoft: '#e3eee0',
  greenSofter: '#eef5eb',
  rust: '#a9341d',
  rustSoft: '#fbeee6',
  orange: '#c66a45',
  amber: '#d8973a',
  plum: '#6e3fb7',
  blue: '#3454c1',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
  font: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
}

export const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

// Format number with decimals
export const formatNumber = (value, decimals = 2) => {
  if (value == null || isNaN(value)) return '-'
  return Number(value).toFixed(decimals)
}

export const formatVisitSplit = (mtcVisits, totalVisits) => {
  const mtc = Number(mtcVisits || 0)
  const total = Number(totalVisits || 0)
  return total > 0 && total !== mtc ? `${mtc} / ${total}` : `${mtc}`
}

// Format percentage
export const formatPercent = (value) => {
  if (value == null || isNaN(value)) return '-'
  return `${Number(value).toFixed(1)}%`
}

// Format date
export const formatDate = (dateStr) => {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export const formatShortDate = (dateStr) => {
  if (!dateStr) return '-'
  const parts = getAspireDateTimeParts(dateStr)
  if (parts) return formatDateParts(parts)
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return '-'
  return date.toLocaleDateString()
}

export const formatShortDateTime = (dateStr) => {
  if (!dateStr) return '-'
  const parts = getAspireDateTimeParts(dateStr)
  if (parts) return parts.hour == null ? formatDateParts(parts) : `${formatDateParts(parts)}, ${formatTimeParts(parts)}`
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return String(dateStr)
  return date.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
}

export const formatTimeOnly = (dateStr) => {
  if (!dateStr) return '-'
  const parts = getAspireDateTimeParts(dateStr)
  if (parts) return formatTimeParts(parts)
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return String(dateStr)
  return formatTimeParts({
    hour: date.getUTCHours(),
    minute: date.getUTCMinutes()
  })
}

export const getAspireDateTimeParts = (value) => {
  if (!value) return null
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null
    return {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
      hour: value.getUTCHours(),
      minute: value.getUTCMinutes()
    }
  }

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/)
  if (!match) return null
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: match[4] == null ? null : Number(match[4]),
    minute: match[5] == null ? null : Number(match[5])
  }
}

export const formatDateParts = ({ year, month, day }) => {
  if (!year || !month || !day) return '-'
  return new Date(year, month - 1, day).toLocaleDateString()
}

export const formatTimeParts = ({ hour, minute }) => {
  if (hour == null || minute == null) return '-'
  const suffix = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour % 12 || 12
  return `${displayHour}:${String(minute).padStart(2, '0')} ${suffix}`
}

export const getCrewLabel = (crew = {}) => {
  const selectedCrew = crew || {}
  return selectedCrew.name || selectedCrew.crewLeaderName || '-'
}

export const getFirstPresent = (source = {}, keys = []) => {
  const row = source || {}
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== '') return row[key]
  }
  return null
}

export const formatCurrencyOrPending = (value) => {
  if (value == null || value === '') return 'Pending data'
  return formatCurrency(value)
}

export const getVisitDateValue = (visit = {}) => {
  return getFirstPresent(visit, [
    'date',
    'completedAt',
    'completedDate',
    'scheduledDate',
    'scheduledStartDate',
    'ScheduledStartDate',
    'ScheduledDate',
    'VisitDate',
    'ArrivalTime',
    'DepartureTime',
    'CompleteDate',
    'ApprovedDate',
    'startDate',
    'visitDate'
  ])
}

export const formatVisitDateTime = (visit = {}) => {
  const dateValue = getVisitDateValue(visit)
  if (!dateValue) return 'Date pending'

  const date = new Date(dateValue)
  const dateParts = getAspireDateTimeParts(dateValue)
  const dateLabel = dateParts ? formatDateParts(dateParts) : isNaN(date.getTime()) ? String(dateValue) : date.toLocaleDateString()
  const startTime = getFirstPresent(visit, ['arrivalTime', 'ArrivalTime', 'startTime', 'StartTime', 'scheduledStartTime'])
  const endTime = getFirstPresent(visit, ['departureTime', 'DepartureTime', 'endTime', 'EndTime', 'scheduledEndTime'])

  if (startTime && endTime) return `${dateLabel} · ${formatTimeOnly(startTime)}-${formatTimeOnly(endTime)}`
  if (startTime) return `${dateLabel} · ${formatTimeOnly(startTime)}`
  if (dateParts && dateParts.hour != null) {
    return `${dateLabel}, ${formatTimeParts(dateParts)}`
  }
  if (!isNaN(date.getTime()) && (date.getUTCHours() || date.getUTCMinutes())) {
    return `${dateLabel}, ${formatTimeOnly(date)}`
  }
  return dateLabel
}

export const getVisitCrewName = (visit = {}) => {
  return getFirstPresent(visit, [
    'activeRouteCrewLeaderName',
    'resolvedCrewLeaderName',
    'resolvedRouteCrewLeaderName',
    'routeCrewLeaderName',
    'CrewLeaderName',
    'crewLeaderName',
    'crewName',
    'foremanName',
    'assignedCrewName',
    'routeName',
    'RouteName',
    'PrimaryContactFullName',
    'ContactName'
  ]) || visit.crew?.name || 'Crew pending'
}

export const getRawVisit = (visit = {}) => visit?.raw && typeof visit.raw === 'object' ? visit.raw : visit

export const getWorkTicketId = (visit = {}) => {
  const row = getRawVisit(visit)
  return getFirstPresent(row, ['workTicketId', 'WorkTicketID']) || getFirstPresent(visit, ['workTicketId', 'WorkTicketID'])
}

export const getWorkTicketNumber = (visit = {}) => {
  const row = getRawVisit(visit)
  return getFirstPresent(row, ['workTicketNumber', 'WorkTicketNumber']) || getFirstPresent(visit, ['ticket'])
}

export const getWorkTicketVisitId = (visit = {}) => {
  const row = getRawVisit(visit)
  return getFirstPresent(row, [
    'workTicketVisitId',
    'workTicketVisitID',
    'WorkTicketVisitID',
    'WorkTicketVisitId',
    'VisitID',
    'visitId'
  ])
}

export const getAspireWorkTicketUrl = (visit = {}) => {
  const workTicketId = getWorkTicketId(visit)
  return workTicketId ? `https://cloud.youraspire.com/app/worktickets/details/${workTicketId}` : null
}

export const getAspireWorkTicketVisitUrl = (visit = {}) => {
  const workTicketVisitId = getWorkTicketVisitId(visit)
  return workTicketVisitId ? `https://cloud.youraspire.com/app/worktickets/visit/${workTicketVisitId}` : null
}

export const getTicketLabel = (visit = {}) => {
  const ticketNumber = getWorkTicketNumber(visit)
  const ticketId = getWorkTicketId(visit)
  if (ticketNumber && ticketNumber !== '-') return `TX ${ticketNumber}`
  if (ticketId) return `Work Ticket ${ticketId}`
  return null
}

export const getVisitCrewCountForNormalization = (visit = {}) => {
  const row = getRawVisit(visit)
  const entries = getCrewTimeEntries(visit)
  const distinctClockContacts = new Set(
    entries
      .filter(entry => !entry.isDriveTime)
      .map(entry => getFirstPresent(entry, ['contactId', 'ContactID', 'employeeId', 'EmployeeID']))
      .filter(Boolean)
  )
  if (distinctClockContacts.size > 1) return distinctClockContacts.size

  const actualCrewCount = safeNumber(getFirstPresent(row, ['actualCrewCount', 'ActualCrewCount']) || getFirstPresent(visit, ['actualCrewCount', 'ActualCrewCount']), 0)
  if (actualCrewCount > 0) return actualCrewCount

  const routeCrewCount = safeNumber(getFirstPresent(row, [
    'activeRouteCrewSize',
    'routeCrewSize',
    'resolvedRouteCrewSize',
    'RouteSize',
    'routeSize'
  ]) || getFirstPresent(visit, [
    'activeRouteCrewSize',
    'routeCrewSize',
    'resolvedRouteCrewSize',
    'RouteSize',
    'routeSize'
  ]), 0)
  if (routeCrewCount > 0 && routeCrewCount <= 12) return routeCrewCount

  const scheduledCrewCount = safeNumber(getFirstPresent(row, ['CrewMembers', 'crewMembers']) || getFirstPresent(visit, ['CrewMembers', 'crewMembers']), 0)
  if (scheduledCrewCount > 0 && scheduledCrewCount <= 2) return scheduledCrewCount

  return 3
}

export const getCrewHourFallback = (visit = {}) => {
  const row = getRawVisit(visit)
  const onSiteCrewHours = safeNumber(getFirstPresent(row, [
    'actualOnSiteHours',
    'ActualOnSiteHours',
    'OnSiteHours',
    'TicketOnSiteHours'
  ]), 0)
  if (onSiteCrewHours <= 0) return 0

  const crewCount = getVisitCrewCountForNormalization(visit)
  return crewCount > 1 ? onSiteCrewHours / crewCount : onSiteCrewHours
}

export const getVisitHours = (visit = {}) => {
  const clockDuration = getVisitClockDurationHours(visit)
  if (clockDuration > 0) return clockDuration

  const crewHourFallback = getCrewHourFallback(visit)
  if (crewHourFallback > 0) return crewHourFallback

  const rawManHours = safeNumber(getFirstPresent(visit, [
    'HoursAct',
    'Hours',
    'hours',
    'HoursEst',
    'estimatedManHours'
  ]), 0)
  if (rawManHours > 0) {
    const crewCount = getVisitCrewCountForNormalization(visit)
    return crewCount > 1 ? rawManHours / crewCount : rawManHours
  }

  return getFirstPresent(visit, ['estimatedHours'])
}

export const getVisitStatus = (visit = {}) => getFirstPresent(visit, [
  'WorkTicketStatusName',
  'WorkTicketStatus',
  'statusName',
  'status'
])

export const getCrewTimeEntries = (visit = {}) => {
  const row = getRawVisit(visit)
  const entries = row.crewTimeEntries || row.CrewTimeEntries || visit.crewTimeEntries || []
  return Array.isArray(entries) ? entries : []
}

export const getClockEntryName = (entry = {}) => {
  return getFirstPresent(entry, [
    'contactName',
    'ContactName',
    'crewMemberName',
    'CrewMemberName',
    'employeeName',
    'EmployeeName',
    'name',
    'Name'
  ]) || [entry.firstName || entry.FirstName, entry.lastName || entry.LastName].filter(Boolean).join(' ')
}

export const getInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Stable color picker so each crew member gets a consistent chip color
// across visits.
const PEOPLE_CHIP_COLORS = CREW_COLORS
export const colorForName = (name = '') => {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0
  return PEOPLE_CHIP_COLORS[Math.abs(hash) % PEOPLE_CHIP_COLORS.length]
}

// Aspire time-entry URL for a visit: jumps to the daily time-entry detail page
// for the work ticket on the visit's completed/scheduled date.
// Pattern: https://cloud.youraspire.com/app/timeentry/details/YYYY-MM-DD/0/0/<WorkTicketID>
export const getAspireTimeEntryUrl = (visit = {}) => {
  const raw = getRawVisit(visit)
  const dateValue = getVisitDateValue(raw) || getVisitDateValue(visit)
  const ticketId = getWorkTicketId(raw) || getWorkTicketId(visit)
  if (!dateValue || !ticketId) return null
  // If the source already looks like a YYYY-MM-DD prefix, trust it verbatim —
  // Aspire-issued visit dates are calendar dates, not timezone-bearing instants,
  // so any timezone interpretation can shift them by a day.
  const dateStr = String(dateValue)
  const prefixMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/)
  let yyyy; let mm; let dd
  if (prefixMatch) {
    [, yyyy, mm, dd] = prefixMatch
  } else {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return null
    yyyy = date.getUTCFullYear()
    mm = String(date.getUTCMonth() + 1).padStart(2, '0')
    dd = String(date.getUTCDate()).padStart(2, '0')
  }
  return `https://cloud.youraspire.com/app/timeentry/details/${yyyy}-${mm}-${dd}/0/0/${ticketId}`
}

export const getVisitPeopleChips = (visit = {}) => {
  const names = getCrewTimeEntries(visit)
    .map(getClockEntryName)
    .filter(Boolean)
  const uniqueNames = [...new Set(names)]
  if (!uniqueNames.length) {
    const fallback = visit.crew || getVisitCrewName(getRawVisit(visit))
    return { chips: [], fallback: fallback || null, overflow: 0 }
  }
  // Show every clocked-in crew member by default. The legacy "+N" overflow is
  // kept as a guardrail for pathological visits with very large crews.
  const SHOW = 12
  const chips = uniqueNames.slice(0, SHOW).map(name => ({
    name,
    initials: getInitials(name),
    color: colorForName(name)
  }))
  return { chips, fallback: null, overflow: Math.max(0, uniqueNames.length - SHOW) }
}

export const getVisitTimeRange = (visit = {}) => {
  const row = getRawVisit(visit)
  const start = getFirstPresent(row, ['arrivalTime', 'ArrivalTime', 'startTime', 'StartTime', 'scheduledStartTime'])
  const end = getFirstPresent(row, ['departureTime', 'DepartureTime', 'endTime', 'EndTime', 'scheduledEndTime'])
  if (start || end) return `${start ? formatTimeOnly(start) : '-'}-${end ? formatTimeOnly(end) : '-'}`

  const entries = getCrewTimeEntries(visit)
  const starts = entries.map(entry => getFirstPresent(entry, ['startTime', 'StartTime'])).filter(Boolean)
  const ends = entries.map(entry => getFirstPresent(entry, ['endTime', 'EndTime'])).filter(Boolean)
  if (!starts.length && !ends.length) return null

  const startDates = starts.map(value => new Date(value)).filter(date => !isNaN(date.getTime()))
  const endDates = ends.map(value => new Date(value)).filter(date => !isNaN(date.getTime()))
  const firstStart = startDates.length ? new Date(Math.min(...startDates.map(date => date.getTime()))) : starts[0]
  const lastEnd = endDates.length ? new Date(Math.max(...endDates.map(date => date.getTime()))) : ends[ends.length - 1]
  return `${firstStart ? formatTimeOnly(firstStart) : '-'}-${lastEnd ? formatTimeOnly(lastEnd) : '-'}`
}

export const getTimestampMs = (value) => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return isNaN(date.getTime()) ? null : date.getTime()
}

export const getDurationHours = (start, end) => {
  const startMs = getTimestampMs(start)
  const endMs = getTimestampMs(end)
  if (startMs == null || endMs == null || endMs <= startMs) return null
  return (endMs - startMs) / (60 * 60 * 1000)
}

export const getEntryHours = (entry = {}) => safeNumber(getFirstPresent(entry, ['hours', 'Hours', 'TotalHours']), 0)

export const averagePositive = (values = []) => {
  const positive = values.map(value => safeNumber(value, 0)).filter(value => value > 0)
  if (!positive.length) return 0
  return positive.reduce((sum, value) => sum + value, 0) / positive.length
}

export const getVisitClockDurationHours = (visit = {}) => {
  const row = getRawVisit(visit)
  const start = getFirstPresent(row, ['arrivalTime', 'ArrivalTime', 'startTime', 'StartTime', 'scheduledStartTime'])
  const end = getFirstPresent(row, ['departureTime', 'DepartureTime', 'endTime', 'EndTime', 'scheduledEndTime'])
  const directDuration = getDurationHours(start, end)
  if (directDuration) return directDuration

  const onSiteEntries = getCrewTimeEntries(visit).filter(entry => !entry.isDriveTime)
  const starts = onSiteEntries.map(entry => getTimestampMs(getFirstPresent(entry, ['startTime', 'StartTime']))).filter(value => value != null)
  const ends = onSiteEntries.map(entry => getTimestampMs(getFirstPresent(entry, ['endTime', 'EndTime']))).filter(value => value != null)
  if (starts.length && ends.length) {
    const entryDuration = (Math.max(...ends) - Math.min(...starts)) / (60 * 60 * 1000)
    if (entryDuration > 0) return entryDuration
  }

  return averagePositive(onSiteEntries.map(getEntryHours))
}

export const getVisitActualHours = (visit = {}) => {
  const clockDuration = getVisitClockDurationHours(visit)
  if (clockDuration > 0) return clockDuration

  const crewHourFallback = getCrewHourFallback(visit)
  if (crewHourFallback > 0) return crewHourFallback

  const rawActualManHours = safeNumber(getFirstPresent(visit, [
    'HoursAct',
    'actualHours',
    'ActualHours'
  ]), 0)
  if (rawActualManHours > 0) {
    const crewCount = getVisitCrewCountForNormalization(visit)
    return crewCount > 1 ? rawActualManHours / crewCount : rawActualManHours
  }

  return 0
}

// Associated drive time for a visit: HoursAct - OnSiteHours, as reported by
// Aspire. Returns 0 when either field is missing or the math goes negative
// (clock skew, partial timecards, etc.).
export const getVisitAssociatedDriveHours = (visit = {}) => {
  const row = getRawVisit(visit)
  const hoursAct = safeNumber(getFirstPresent(row, ['HoursAct', 'hoursAct']) ?? getFirstPresent(visit, ['HoursAct', 'hoursAct']), 0)
  const onSite = safeNumber(getFirstPresent(row, ['OnSiteHours', 'onSiteHours', 'ActualOnSiteHours', 'actualOnSiteHours', 'TicketOnSiteHours']) ?? getFirstPresent(visit, ['OnSiteHours', 'onSiteHours', 'ActualOnSiteHours', 'actualOnSiteHours', 'TicketOnSiteHours']), 0)
  if (hoursAct <= 0 || onSite <= 0) return 0
  return Math.max(0, hoursAct - onSite)
}

export const getVisitDriveDurationHours = (visit = {}) => {
  const driveEntries = getCrewTimeEntries(visit).filter(entry => entry.isDriveTime)
  if (!driveEntries.length) return 0

  const starts = driveEntries.map(entry => getTimestampMs(getFirstPresent(entry, ['startTime', 'StartTime']))).filter(value => value != null)
  const ends = driveEntries.map(entry => getTimestampMs(getFirstPresent(entry, ['endTime', 'EndTime']))).filter(value => value != null)
  if (starts.length && ends.length) {
    const entryDuration = (Math.max(...ends) - Math.min(...starts)) / (60 * 60 * 1000)
    if (entryDuration > 0) return entryDuration
  }

  return averagePositive(driveEntries.map(getEntryHours))
}

export const getMedian = (values = []) => {
  const sorted = values.map(value => safeNumber(value, 0)).filter(value => value > 0).sort((a, b) => a - b)
  if (!sorted.length) return 0
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

export const getVisitTrendStats = (visits = [], fallbackAvg = 0) => {
  const recentVisits = visits.filter(visit => visit.wAgo < 4)
  const priorVisits = visits.filter(visit => visit.wAgo >= 4 && visit.wAgo < 12)
  const recentAvg = recentVisits.length
    ? recentVisits.reduce((sum, visit) => sum + visit.hrs, 0) / recentVisits.length
    : fallbackAvg
  const priorAvg = priorVisits.length
    ? priorVisits.reduce((sum, visit) => sum + visit.hrs, 0) / priorVisits.length
    : recentAvg

  return {
    recentAvg,
    trendPct: priorAvg ? Math.round(((recentAvg - priorAvg) / priorAvg) * 100) : 0
  }
}

export const filterVisitRowsByType = (visits = [], typeFilter = 'all') => {
  if (!typeFilter || typeFilter === 'all') return visits
  return visits.filter(visit => visit.type === typeFilter)
}

export const getMaintenanceMetricRows = (visitRows = []) => {
  const actualRows = visitRows.filter(visit => !visit.isEstimated && safeNumber(visit.actualHrs ?? visit.hrs, 0) > 0)
  const maintenanceRows = actualRows.filter(visit => visit.type === 'Maint')
  return maintenanceRows.length ? maintenanceRows : actualRows
}

export const safeNumber = (value, fallback = 0) => {
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

export const pickNumber = (source = {}, keys = [], fallback = 0) => {
  const value = getFirstPresent(source, keys)
  return value === null ? fallback : safeNumber(value, fallback)
}

export const formatCompactCurrency = (value) => {
  const number = safeNumber(value)
  if (Math.abs(number) >= 1000) return `$${(number / 1000).toFixed(1)}k`
  return formatCurrency(number)
}

export const getDayAbbrev = (day) => {
  const value = String(day || '').trim()
  if (!value) return '-'
  return value.slice(0, 3).toUpperCase()
}

export const getServiceTypeKey = (visit = {}) => {
  const service = String(
    getFirstPresent(visit, ['serviceName', 'ServiceName', 'ServiceDisplayName', 'serviceCategory', 'ServiceCategory']) || ''
  ).toLowerCase()
  if (service.includes('irrig')) return 'Irrig'
  if (service.includes('herb')) return 'Herb'
  if (service.includes('maint')) return 'Maint'
  return 'Other'
}

export const getVisitServiceLabel = (visit = {}) => {
  return getFirstPresent(visit, [
    'OpportunityName',
    'opportunityName',
    'ServiceDisplayName',
    'serviceDisplayName',
    'ServiceName',
    'serviceName'
  ])
}

export const getVisitDate = (visit = {}) => {
  const value = getVisitDateValue(visit)
  const date = value ? new Date(value) : null
  return date && !isNaN(date.getTime()) ? date : null
}

export const getWeeksAgo = (date, now = new Date()) => {
  if (!date) return 0
  return Math.max(0, Math.round((now.getTime() - date.getTime()) / (7 * 24 * 60 * 60 * 1000)))
}

export const buildVisitChartRows = (selectedProperty, detailVisits = []) => {
  const sourceVisits = detailVisits.length ? detailVisits : getVisitHistoryRows(selectedProperty)
  const now = new Date()
  const mapped = sourceVisits
    .map((visit, index) => {
      const actualHrs = safeNumber(getVisitActualHours(visit), 0)
      const hrs = actualHrs || safeNumber(getVisitHours(visit), 0)
      const date = getVisitDate(visit)
      const dateValue = getVisitDateValue(visit)
      const type = getServiceTypeKey(visit)
      const isEstimated = actualHrs <= 0 && hrs > 0
      return {
        id: getWorkTicketId(visit) || getWorkTicketNumber(visit) || `${date?.toISOString() || 'visit'}-${index}`,
        wAgo: getWeeksAgo(date, now),
        hrs,
        actualHrs,
        isEstimated,
        type,
        label: type === 'Other' ? getVisitServiceLabel(visit) : null,
        crew: getVisitCrewName(visit),
        ticket: getWorkTicketNumber(visit) || getWorkTicketId(visit) || '-',
        date: dateValue ? formatShortDate(dateValue) : formatVisitDateTime(visit),
        status: getVisitStatus(visit),
        flag: !isEstimated && hrs > safeNumber(selectedProperty?.estimatedHours, 0) && safeNumber(selectedProperty?.estimatedHours, 0) > 0 ? 'high' : null,
        raw: visit
      }
    })
    .filter(row => row.hrs > 0)

  // Dedupe by work-ticket id: server payload sometimes has one entry per
  // crewTimeEntry/WorkTicketTime row, which surfaces as duplicate visit rows
  // here. Keep the entry with the most actual hours per ticket; on a tie,
  // prefer the more specific service type (anything but "Other").
  const TYPE_PRIORITY = { Maint: 3, Irrig: 3, Herb: 3, Other: 0 }
  const byTicket = new Map()
  for (const row of mapped) {
    const key = row.id
    const incumbent = byTicket.get(key)
    if (!incumbent) { byTicket.set(key, row); continue }
    const incHrs = incumbent.hrs
    const newHrs = row.hrs
    if (newHrs > incHrs) byTicket.set(key, row)
    else if (newHrs === incHrs && (TYPE_PRIORITY[row.type] || 0) > (TYPE_PRIORITY[incumbent.type] || 0)) {
      byTicket.set(key, row)
    }
  }
  const rows = Array.from(byTicket.values()).sort((a, b) => a.wAgo - b.wAgo)

  if (rows.length) return rows

  const avgHours = safeNumber(selectedProperty?.avgOnSiteHoursDisplay || selectedProperty?.avgOnSiteHours)
  return Array.from({ length: Math.min(safeNumber(selectedProperty?.completedOccurrences, 12), 12) || 6 }, (_, index) => ({
    id: `fallback-${index}`,
    wAgo: index,
    hrs: avgHours || safeNumber(selectedProperty?.estimatedHours, 1),
    actualHrs: 0,
    isEstimated: true,
    type: index % 5 === 0 ? 'Irrig' : 'Maint',
    crew: getCrewLabel(selectedProperty?.primaryCrewLeader),
    ticket: '-',
    date: index === 0 ? 'Recent' : `${index}w ago`,
    status: 'Complete',
    flag: null,
    raw: {}
  }))
}

export const buildMonthlyRows = (selectedProperty, visitRows = []) => {
  const monthRows = []
  const now = new Date()
  const ticketRows = Array.isArray(selectedProperty?.workTickets) ? selectedProperty.workTickets : []
  const hasTicketFinancials = ticketRows.some(ticket =>
    getFirstPresent(ticket, ['EarnedRevenue', 'earnedRevenue', 'LaborCostAct', 'laborCostAct']) !== null
  )
  const fallbackRevenue = pickNumber(selectedProperty, ['maintenanceEarnedRevenue', 'totalEarnedRevenue', 'totalRevenue'])
  const fallbackLaborCost = pickNumber(selectedProperty, ['maintenanceLaborCost', 'totalLaborCost'])

  for (let index = 11; index >= 0; index -= 1) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - index, 1)
    const key = `${monthDate.getFullYear()}-${monthDate.getMonth()}`
    const visits = visitRows.filter(visit => {
      const date = getVisitDate(visit.raw)
      return date && `${date.getFullYear()}-${date.getMonth()}` === key
    })
    const tickets = hasTicketFinancials
      ? ticketRows.filter(ticket => {
          const date = getVisitDate(ticket)
          return date && `${date.getFullYear()}-${date.getMonth()}` === key
        })
      : []
    const visitCount = visits.length
    const actualHours = visitCount
      ? visits.reduce((sum, visit) => sum + visit.hrs, 0) / visitCount
      : safeNumber(selectedProperty?.avgOnSiteHoursDisplay || selectedProperty?.avgOnSiteHours)
    const monthlyRevenue = hasTicketFinancials
      ? tickets.reduce((sum, ticket) => sum + pickNumber(ticket, ['EarnedRevenue', 'earnedRevenue']), 0)
      : fallbackRevenue / 12
    const monthlyCost = hasTicketFinancials
      ? tickets.reduce((sum, ticket) => sum + pickNumber(ticket, ['LaborCostAct', 'laborCostAct']), 0)
      : fallbackLaborCost / 12
    monthRows.push({
      key,
      short: monthDate.toLocaleString([], { month: 'short' }).slice(0, 1),
      label: monthDate.toLocaleString([], { month: 'short' }),
      est: safeNumber(selectedProperty?.estimatedHours),
      act: actualHours,
      rev: monthlyRevenue,
      othRev: 0,
      cost: monthlyCost,
      visits: visitCount || tickets.length || (!hasTicketFinancials ? Math.max(0, Math.round(safeNumber(selectedProperty?.completedOccurrences) / 12)) : 0),
      current: index === 0
    })
  }

  return monthRows
}

export const buildServiceRows = (selectedProperty = {}) => {
  const irrigation = getServiceDetail(selectedProperty, 'Irrigation')
  const herbicide = getServiceDetail(selectedProperty, 'Herbicide')
  const maintenanceCompleted = Math.max(0, safeNumber(selectedProperty.completedOccurrences) - irrigation.completed - herbicide.completed)
  const maintenanceFuture = Math.max(0, safeNumber(selectedProperty.futureTicketCount || selectedProperty.futureVisits?.length) - irrigation.future - herbicide.future)
  const maintenanceHours = Math.max(0, safeNumber(selectedProperty.totalOnSiteHours) - safeNumber(irrigation.hours) - safeNumber(herbicide.hours))
  const maintenanceCost = Math.max(0, safeNumber(selectedProperty.totalLaborCost) - safeNumber(irrigation.cost) - safeNumber(herbicide.cost))
  const rows = [
    {
      name: 'Irrigation',
      tickets: irrigation.tickets,
      completed: irrigation.completed,
      future: irrigation.future,
      hours: safeNumber(irrigation.hours),
      cost: irrigation.cost
    },
    {
      name: 'Herbicide',
      tickets: herbicide.tickets,
      completed: herbicide.completed,
      future: herbicide.future,
      hours: safeNumber(herbicide.hours),
      cost: herbicide.cost
    },
    {
      name: 'Maintenance',
      tickets: maintenanceCompleted + maintenanceFuture,
      completed: maintenanceCompleted,
      future: maintenanceFuture,
      hours: maintenanceHours,
      cost: maintenanceCost
    }
  ].filter(service => service.tickets > 0 || service.hours > 0)

  const totalHours = rows.reduce((sum, service) => sum + service.hours, 0) || 1
  return rows.map(service => ({
    ...service,
    share: service.hours / totalHours
  }))
}

export const inferFrequencyFromTicketCount = (ticketCount = 0) => {
  const count = safeNumber(ticketCount)
  if (count <= 0) return 'none'
  if (count >= 36) return 'weekly'
  if (count >= 18) return 'biweekly'
  if (count >= 8) return 'monthly'
  if (count >= 3) return 'quarterly'
  if (count >= 2) return 'semiannual'
  return 'annual'
}

export const getPropertyServiceTicketCounts = (property = {}) => {
  const irrigationCompleted = safeNumber(property.irrigationCompletedVisits)
  const irrigationFuture = safeNumber(property.irrigationFutureVisits)
  const herbicideCompleted = safeNumber(property.herbicideCompletedVisits)
  const herbicideFuture = safeNumber(property.herbicideFutureVisits)
  const completedTotal = safeNumber(property.completedOccurrences)
  const futureTotal = safeNumber(property.futureTicketCount || property.futureVisits?.length)

  return {
    maintenance: Math.max(0, completedTotal + futureTotal - irrigationCompleted - irrigationFuture - herbicideCompleted - herbicideFuture),
    irrigation: irrigationCompleted + irrigationFuture,
    herbicide: herbicideCompleted + herbicideFuture
  }
}

export const inferPropertyEditDefaults = (property = {}) => {
  const counts = getPropertyServiceTicketCounts(property)
  return {
    mtcFrequency: property.mtcFrequency || inferFrequencyFromTicketCount(counts.maintenance),
    irrigationCheckFrequency: property.irrigationCheckFrequency || inferFrequencyFromTicketCount(counts.irrigation),
    herbicideApplicationFrequency: property.herbicideApplicationFrequency || inferFrequencyFromTicketCount(counts.herbicide),
    noIrrigation: property.noIrrigation != null ? !!property.noIrrigation : counts.irrigation <= 0
  }
}

export const getPrimaryCrewName = (property = {}) => {
  return getCrewLabel(property.primaryCrewLeader) !== '-'
    ? getCrewLabel(property.primaryCrewLeader)
    : getCrewLabel(property.assignedCrewLeaders?.[0] || property.crewLeaders?.[0])
}

export const buildSidebarData = (selectedProperty, detailHistory, geoInsights) => {
  const property = selectedProperty || {}
  const visitRows = buildVisitChartRows(property, detailHistory.visits || [])
  const metricVisitRows = getMaintenanceMetricRows(visitRows)
  const monthlyRows = buildMonthlyRows(property, metricVisitRows)
  const serviceRows = buildServiceRows(property)
  const completedVisits = safeNumber(property.completedOccurrences)
  const annualVisits = safeNumber(property.targetAnnualVisits || property.annualOccurrences)
  const futureVisits = safeNumber(property.futureVisits?.length || property.futureTicketCount)
  const normalizedVisitHours = metricVisitRows.map(visit => visit.hrs).filter(hours => hours > 0)
  const normalizedAvgHours = averagePositive(normalizedVisitHours)
  const normalizedMedianHours = getMedian(normalizedVisitHours)
  const fallbackMedianHours = safeNumber(property.medianOnSiteHoursDisplay || property.medianOnSiteHours)
  const avgHours = normalizedAvgHours || safeNumber(property.avgOnSiteHoursDisplay || property.avgOnSiteHours)
  const estHours = safeNumber(property.estimatedHours)
  const prescribedManHours = safeNumber(property.prescribedManHours)
  const assignedCrewSize = safeNumber(property.assignedRouteCrewSize || property.internalEstimateCrewCount, 3) || 3
  const prescribedClockHours = prescribedManHours > 0 ? prescribedManHours / assignedCrewSize : 0
  // History-backed internal estimate (man-hour baseline ÷ median crew) —
  // preferred for display over the Aspire contract figure when present.
  const internalEstManHours = safeNumber(property.internalEstimatedManHours)
  const internalEstHours = internalEstManHours > 0 && assignedCrewSize > 0
    ? internalEstManHours / assignedCrewSize
    : safeNumber(property.internalEstimatedHours)
  const internalEstSource = property.internalEstimateSource || null
  const hasInternalEst = internalEstHours > 0 && internalEstSource && internalEstSource !== 'aspire'
  const normalizedDriveHours = averagePositive(metricVisitRows.map(getVisitDriveDurationHours))
  const rolledUpTotalActualHours = safeNumber(property.avgActualHours || property.hoursActual || property.avgOnSiteHoursDisplay || property.avgOnSiteHours)
  const rolledUpDriveActual = Math.max(0, rolledUpTotalActualHours - safeNumber(property.avgOnSiteHoursDisplay || property.avgOnSiteHours))
  const driveActual = normalizedDriveHours || rolledUpDriveActual
  const totalActualHours = avgHours + driveActual
  const driveExpected = safeNumber(property.driveExpectedHours, geoInsights?.yardRoute?.durationMinutes ? geoInsights.yardRoute.durationMinutes / 60 : safeNumber(geoInsights?.yardDriveMinutes) / 60)
  const totalEarnedRev = pickNumber(property, ['totalEarnedRevenue', 'totalRevenue'])
  const maintenanceEarnedRev = getFirstPresent(property, ['maintenanceEarnedRevenue']) === null
    ? totalEarnedRev
    : pickNumber(property, ['maintenanceEarnedRevenue'])
  const otherEarnedRev = pickNumber(property, ['otherEarnedRevenue'])
  const totalRev = getFirstPresent(property, ['totalEarnedRevenue', 'totalRevenue']) === null
    ? maintenanceEarnedRev + otherEarnedRev
    : totalEarnedRev
  const estLabor = pickNumber(property, ['estimatedLaborCost'])
  const actualLabor = pickNumber(property, ['maintenanceLaborCost', 'totalLaborCost'])
  const costVariance = getFirstPresent(property, ['costVariance']) === null
    ? estLabor - actualLabor
    : pickNumber(property, ['costVariance'])
  const netProfit = getFirstPresent(property, ['netProfit']) === null
    ? maintenanceEarnedRev - actualLabor
    : pickNumber(property, ['netProfit'])
  const crewName = getPrimaryCrewName(property)
  const scheduledDay = property.scheduledDays?.[0] || property.futureVisits?.[0]?.dayOfWeek || property.day || 'Unscheduled'

  return {
    property,
    propertyId: getAspirePropertyId(property),
    aspireUrl: getAspirePropertyUrl(property),
    address: [property.address1, property.address2].filter(Boolean).join(' '),
    crewName,
    scheduledDay,
    // Scenario context (set when the drawer is opened from the refine planner):
    // parked = scenario-assigned to a crew outside the active maintenance pool;
    // scenarioUnscheduled = on the scenario route but not placed on a day.
    parkedCrewName: property.parkedCrewName || null,
    parkedCrewInactive: property.parkedCrewInactive ?? null,
    parkedCrewDivision: property.parkedCrewDivision || null,
    scenarioUnscheduled: !!property.scenarioUnscheduled,
    companyChip: property.companyId ? `${property.companyName || 'Company'} #${property.companyId}` : null,
    visitRows,
    metricVisitRows,
    monthlyRows,
    serviceRows,
    kpis: {
      margin: safeNumber(property.profitMargin),
      netProfit,
      earnedRev: maintenanceEarnedRev,
      othRev: otherEarnedRev,
      totalRev,
      estLabor,
      actualLabor,
      costVariance,
      estHoursPerVisit: estHours,
      prescribedManHours,
      prescribedClockHours,
      prescribedCrewSize: assignedCrewSize,
      hasPrescribedHours: prescribedManHours > 0,
      internalEstHours,
      internalEstSource,
      internalEstVisitCount: safeNumber(property.internalEstimateVisitCount),
      hasInternalEst,
      avgHours,
      medianHours: normalizedMedianHours || fallbackMedianHours,
      normalizedMedianHours,
      normalizedVisitCount: normalizedVisitHours.length,
      fallbackMedianHours,
      estVariance: estHours - avgHours,
      otHours: safeNumber(property.totalOTHours),
      completedVisits,
      annualVisits,
      futureVisits,
      hoursActual: totalActualHours,
      driveActual,
      driveExpected
    }
  }
}

export const getPropertyCoordinates = (row = {}) => {
  const property = row || {}
  const lat = Number(property.latitude ?? property.location?.coordinates?.[1])
  const lng = Number(property.longitude ?? property.location?.coordinates?.[0])
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  if (lat === 0 && lng === 0) return null
  return { lat, lng }
}

export const formatMiles = (miles) => {
  const value = Number(miles)
  if (!Number.isFinite(value)) return '-'
  return `${value.toFixed(value < 10 ? 1 : 0)} mi`
}

const estimateRoadMiles = (straightLineMiles) => {
  const miles = Number(straightLineMiles)
  if (!Number.isFinite(miles)) return null
  return miles <= 0 ? 0 : miles * 1.25
}

const estimateDriveMinutes = (roadMiles) => {
  const miles = Number(roadMiles)
  if (!Number.isFinite(miles)) return null
  if (miles <= 0) return 0
  return Math.max(4, Math.round((miles / 32) * 60))
}

export const buildGeoInsights = (selectedProperty, allRows = []) => {
  const selectedCoords = getPropertyCoordinates(selectedProperty)
  if (!selectedCoords) {
    return {
      hasCoordinates: false,
      selectedCoords: null,
      nearestSite: null,
      sitesWithinFiveMiles: [],
      sitesWithinThreeMiles: [],
      sitesWithinOneMile: [],
      avgDistanceWithinFiveMiles: null,
      medianDistanceWithinFiveMiles: null,
      yardStraightMiles: null,
      yardRoadMiles: null,
      yardDriveMinutes: null
    }
  }

  const selectedId = String(getAspirePropertyId(selectedProperty) || selectedProperty?._id || '')
  const nearbySites = allRows
    .map(row => {
      const coords = getPropertyCoordinates(row)
      const rowId = String(getAspirePropertyId(row) || row?._id || '')
      if (!coords || (selectedId && rowId === selectedId)) return null
      const distanceMiles = haversineMiles(selectedCoords.lat, selectedCoords.lng, coords.lat, coords.lng)
      return { ...row, distanceMiles }
    })
    .filter(Boolean)
    .sort((a, b) => a.distanceMiles - b.distanceMiles)

  const yardStraightMiles = haversineMiles(HMI_YARD.lat, HMI_YARD.lng, selectedCoords.lat, selectedCoords.lng)
  const yardRoadMiles = estimateRoadMiles(yardStraightMiles)
  const sitesWithinFiveMiles = nearbySites.filter(site => site.distanceMiles <= 5)
  const withinFiveDistances = sitesWithinFiveMiles.map(site => site.distanceMiles).sort((a, b) => a - b)
  const avgDistanceWithinFiveMiles = withinFiveDistances.length
    ? withinFiveDistances.reduce((sum, distance) => sum + distance, 0) / withinFiveDistances.length
    : null
  const medianDistanceWithinFiveMiles = withinFiveDistances.length
    ? withinFiveDistances[Math.floor(withinFiveDistances.length / 2)]
    : null

  return {
    hasCoordinates: true,
    selectedCoords,
    nearestSite: nearbySites[0] || null,
    sitesWithinFiveMiles,
    sitesWithinThreeMiles: nearbySites.filter(site => site.distanceMiles <= 3),
    sitesWithinOneMile: nearbySites.filter(site => site.distanceMiles <= 1),
    avgDistanceWithinFiveMiles,
    medianDistanceWithinFiveMiles,
    yardStraightMiles,
    yardRoadMiles,
    yardDriveMinutes: estimateDriveMinutes(yardRoadMiles)
  }
}

export const getVisitHistoryRows = (property = {}) => {
  const row = property || {}
  return getFirstPresent(row, [
    'visitHistory',
    'completedVisits',
    'recentVisits',
    'pastVisits',
    'visits'
  ]) || []
}

export const getServiceDetail = (property = {}, service) => {
  const prefix = service.toLowerCase()
  const displayName = service === 'Irrigation' ? 'Irrigation' : 'Herbicide'
  const completed = Number(property?.[`${prefix}CompletedVisits`] || 0)
  const future = Number(property?.[`${prefix}FutureVisits`] || 0)
  const hours = property?.[`${prefix}Hours`]
  const cost = getFirstPresent(property, [
    `${prefix}Cost`,
    `${prefix}LaborCost`,
    `${prefix}TotalCost`,
    `${prefix}TotalLaborCost`,
    `total${displayName}Cost`,
    `total${displayName}LaborCost`
  ])

  return {
    name: displayName,
    tickets: completed + future,
    completed,
    future,
    hours,
    cost
  }
}

export const normalizeRoutingRow = (row = {}) => {
  const estimatedHours = Number(row.estimatedHours || 0)
  const prescribedManHours = row.prescribedManHours != null ? Number(row.prescribedManHours) : null
  const internalEstHoursDisplay = Number(row.internalEstimatedHours || 0)
  const avgOnSiteHoursDisplay = Number(row.avgOnSiteHours || row.avgActualHours || 0)
  const medianOnSiteHoursDisplay = Number(row.medianOnSiteHours || row.medianActualHours || 0)
  const companyId = row.companyId || row.CompanyID || null
  const companyName = row.companyName || row.CompanyName || ''
  const totalEarnedRevenue = pickNumber(row, ['totalEarnedRevenue', 'totalRevenue'])
  const contractPricePerVisit = Number(row.contractPricePerVisit || 0)
  const annualTicketCount = Number(row.annualOccurrences || 0)
  const targetAnnualVisits = Number(row.targetAnnualVisits || annualTicketCount || 0)
  const annualVisitsDisplay = targetAnnualVisits || annualTicketCount
  const completedMtcVisits = Number(row.completedOccurrences || 0)
  const herbicideCompletedVisits = Number(row.herbicideCompletedVisits || 0)
  const irrigationCompletedVisits = Number(row.irrigationCompletedVisits || 0)
  const herbicideAnnualVisits = herbicideCompletedVisits + Number(row.herbicideFutureVisits || 0)
  const irrigationAnnualVisits = irrigationCompletedVisits + Number(row.irrigationFutureVisits || 0)
  const completedTotalVisits = completedMtcVisits + herbicideCompletedVisits + irrigationCompletedVisits
  const annualTotalVisits = annualVisitsDisplay + herbicideAnnualVisits + irrigationAnnualVisits
  const estimatedRevenueFromPlan = contractPricePerVisit > 0 && targetAnnualVisits > 0 ? contractPricePerVisit * targetAnnualVisits : 0
  const totalEstimatedRevenue = Number(estimatedRevenueFromPlan || row.totalEstimatedRevenue || 0)

  const maintenanceEarnedRevenue = getFirstPresent(row, ['maintenanceEarnedRevenue']) === null
    ? totalEarnedRevenue
    : pickNumber(row, ['maintenanceEarnedRevenue'])
  const maintenanceLaborCost = pickNumber(row, ['maintenanceLaborCost', 'totalLaborCost'])
  const netProfit = getFirstPresent(row, ['netProfit']) === null
    ? maintenanceEarnedRevenue - maintenanceLaborCost
    : pickNumber(row, ['netProfit'])

  return {
    ...row,
    companyId,
    companyName,
    prescribedManHours,
    companyLabel: companyId
      ? `${companyName || 'Company'} #${companyId}`
      : 'No company',
    estimatedHours,
    internalEstHoursDisplay,
    avgOnSiteHoursDisplay,
    medianOnSiteHoursDisplay,
    onSiteVariance: estimatedHours - avgOnSiteHoursDisplay,
    completedMtcVisits,
    completedTotalVisits,
    herbicideCompletedVisits,
    irrigationCompletedVisits,
    herbicideAnnualVisits,
    irrigationAnnualVisits,
    annualTicketCount,
    targetAnnualVisits,
    annualVisitsDisplay,
    annualTotalVisits,
    estimatedRevenueFromPlan,
    contractPricePerVisit,
    totalEstimatedRevenue,
    maintenanceEarnedRevenue,
    maintenanceLaborCost,
    netProfit,
    totalEarnedRevenue
  }
}

export const getAspirePropertyId = (row = {}) => {
  const property = row || {}
  return property.aspirePropertyId || property.propertyId || property.PropertyID || null
}

export const getAspirePropertyUrl = (row = {}) => {
  const propertyId = getAspirePropertyId(row)
  return propertyId ? `https://cloud.youraspire.com/app/properties/overview/${propertyId}` : null
}

export const isRowProblematic = (row) => {
  return row.netProfit < 0 || row.onSiteVariance < -2 || row.profitMargin < -50
}

// ─── Profitability calculator ──────────────────────────────────────────────
// Pure derivation powering the Profitability drawer tab. Takes the object from
// buildSidebarData() and returns a read-only result driving the 3-card panel.
// Math is locked per spec 2026-06-22-property-profitability-calculator (client).

// Company-wide target margin (ratio). Single constant for v1 — structured so it
// can later become a per-division lookup.
export const PROFIT_TARGET = 0.50
// Below this completed-visit count, history is too thin → 'sparse' state wins.
export const SPARSE_MIN_VISITS = 8
// If the blended price lift exceeds this, surface the multi-year glide note.
export const PHASE_PRICE_PCT = 0.15

const SERVICE_KEYS = [
  { key: 'irrigation', name: 'Irrigation' },
  { key: 'herbicide', name: 'Herbicide' },
  { key: 'maintenance', name: 'Maintenance' }
]

const roundTo = (value, step) => Math.round(value / step) * step

/**
 * Derive the profitability panel model from a sidebar-data object.
 * @param {object} sidebarData - output of buildSidebarData()
 * @param {object} [config] - { target, sparseMinVisits, phasePricePct }
 * @returns {object} panel model (see spec §7)
 */
export const deriveProfitability = (sidebarData = {}, config = {}) => {
  const target = config.target != null ? config.target : PROFIT_TARGET
  const sparseMin = config.sparseMinVisits != null ? config.sparseMinVisits : SPARSE_MIN_VISITS
  const phasePricePct = config.phasePricePct != null ? config.phasePricePct : PHASE_PRICE_PCT

  const property = sidebarData.property || {}
  const kpis = sidebarData.kpis || {}
  const serviceRows = sidebarData.serviceRows || []

  const completed = safeNumber(kpis.completedVisits)
  const targetPct = Math.round(target * 100)

  // ── Annual P&L (Card 1). Revenue anchored to current contract value. ──
  // contractPricePerVisit × annual visits is the cleanest "what we're paid".
  const contractPricePerVisit = safeNumber(property.contractPricePerVisit)
  const annualVisits = safeNumber(kpis.annualVisits)
  const revenue = contractPricePerVisit > 0 && annualVisits > 0
    ? contractPricePerVisit * annualVisits
    : safeNumber(property.totalEstimatedRevenue) || safeNumber(kpis.totalRev)

  const findRow = (name) => serviceRows.find(r => r.name === name) || {}
  const irrigationCost = safeNumber(findRow('Irrigation').cost)
  const herbicideCost = safeNumber(findRow('Herbicide').cost)
  const maintenanceCost = safeNumber(findRow('Maintenance').cost)
  const directCost = safeNumber(property.totalMaterialCost) +
    safeNumber(property.totalEquipmentCost) + safeNumber(property.totalSubCost)

  const costLines = [
    ['Irrigation labor', irrigationCost],
    ['Herbicide labor', herbicideCost],
    ['Maintenance labor', maintenanceCost],
    ['Materials · equip · sub', directCost]
  ]
  const totalCost = costLines.reduce((sum, [, cost]) => sum + cost, 0)
  const profit = revenue - totalCost
  const margin = revenue > 0 ? profit / revenue : 0
  const marginPct = Math.round(margin * 100)
  const gapPts = Math.abs(targetPct - marginPct)

  // ── State ──
  let state
  if (completed < sparseMin) state = 'sparse'
  else if (margin >= target) state = 'healthy'
  else state = 'below'

  const pnl = {
    revenue,
    lines: costLines.map(([label, cost]) => [label, -cost]),
    profit,
    marginPct
  }

  // ── Card 3 rates: loaded cost/man-hr held to (1 - target) of price. ──
  const rateFor = (rowName, costKey) => {
    const row = findRow(rowName)
    const manHours = safeNumber(row.hours)
    const cost = safeNumber(row.cost)
    if (manHours <= 0 || cost <= 0) {
      return { key: costKey, name: rowName, rate: null }
    }
    const loadedPerHour = cost / manHours
    const rate = loadedPerHour / (1 - target)
    return { key: costKey, name: rowName, rate, manHours, cost }
  }
  const maintRate = rateFor('Maintenance', 'maintenance')
  const maintenanceRate = {
    ...maintRate,
    perVisit: maintRate.rate != null && annualVisits > 0
      ? maintRate.cost / annualVisits / (1 - target)
      : (maintRate.rate != null && completed > 0 ? maintRate.cost / completed / (1 - target) : null)
  }
  const rates = state === 'sparse'
    ? SERVICE_KEYS.map(s => ({ key: s.key, name: s.name, rate: null }))
    : [
        { ...rateFor('Irrigation', 'irrigation'), ok: state === 'healthy' },
        { ...rateFor('Herbicide', 'herbicide'), ok: state === 'healthy' },
        { ...maintenanceRate, ok: state === 'healthy' }
      ]

  const result = {
    state,
    margin: marginPct,
    target: targetPct,
    gapPts,
    pnl,
    rates,
    completed,
    levers: null,
    phaseThisYear: null,
    headroom: null
  }

  // ── Card 2: path to target (below only) ──
  if (state === 'below' && revenue > 0 && totalCost > 0) {
    // Price-only revenue and efficiency-only cost to hit target, then apply
    // HALF of each move (50/50 blend) — the two halves recombine to ≈ target.
    const priceOnlyRevenue = totalCost / (1 - target)        // Rp
    const effOnlyCost = revenue * (1 - target)               // Ce
    const blendedRevenue = revenue * (1 + (priceOnlyRevenue / revenue - 1) / 2)   // R'
    const blendedCost = totalCost * (1 - (1 - effOnlyCost / totalCost) / 2)       // C'
    const combined = blendedRevenue > 0
      ? Math.round(((blendedRevenue - blendedCost) / blendedRevenue) * 100)
      : targetPct

    // Translate to per-visit levers the manager quotes.
    const priceFrom = contractPricePerVisit
    const priceTo = roundTo(priceFrom * (blendedRevenue / revenue), 5)
    const pricePct = priceFrom > 0 ? Math.round((priceTo / priceFrom - 1) * 100) : 0
    const hoursFrom = safeNumber(kpis.avgHours)
    const hoursTo = roundTo(hoursFrom * (blendedCost / totalCost), 0.1)

    result.levers = {
      price: { from: priceFrom, to: priceTo, pct: pricePct, unit: '/ visit' },
      time: { from: hoursFrom, to: hoursTo, unit: '/ visit' },
      combined
    }

    // Steep one-renewal price lift → suggest a multi-year glide.
    if (priceFrom > 0 && (priceTo / priceFrom - 1) > phasePricePct) {
      // Margin reachable with HALF of this year's blended price move.
      const phaseRevenue = revenue * (1 + (blendedRevenue / revenue - 1) / 2)
      result.phaseThisYear = phaseRevenue > 0
        ? Math.round(((phaseRevenue - totalCost) / phaseRevenue) * 100)
        : marginPct
    }
  }

  // ── Card 2: healthy headroom (how much cushion before dropping below) ──
  if (state === 'healthy' && revenue > 0) {
    // Revenue could fall to effOnlyCost/(... ) before margin hits target; express
    // the cushion as extra hours/visit and annual dollars of slack.
    const breakevenCost = revenue * (1 - target)             // cost at which margin == target
    const slackDollars = Math.max(0, breakevenCost - totalCost)
    const hoursFrom = safeNumber(kpis.avgHours)
    const extraHours = totalCost > 0 && hoursFrom > 0
      ? roundTo(hoursFrom * (slackDollars / totalCost), 0.1)
      : 0
    result.headroom = { hours: extraHours, dollars: Math.round(slackDollars) }
  }

  return result
}
